// An artifact's Download, one implementation for its pane's title menu and
// for the agent run's Files panel row (Gerry, Oct 6: "how come doc types and
// tbl and diagrams don't show the download option in the right file
// panel?"). A table saves its sheet as CSV, a text or document artifact its
// text, a produced file the file itself. A diagram saves the SVG its pane
// has drawn, so only its pane offers it: a row has no drawing to save.
import fpFetch from "@/services/fpFetch";
import {
  artifactUrls,
  type ArtifactItem,
} from "@/components/FpPages/Artifacts/useArtifacts";
import {
  parseTableContent,
  sheetToCsv,
  type TableArtifactContent,
} from "./tableArtifact";

/** Hands `blob` to the browser as a file named `filename`. */
export const saveBlobAs = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

/** A table's sheet as the CSV its Download saves. */
export const saveTableSheet = (
  table: TableArtifactContent,
  sheetIndex: number,
  title: string
): void => {
  const sheet = table.sheets[Math.min(sheetIndex, table.sheets.length - 1)];
  if (!sheet) return;
  saveBlobAs(
    new Blob([sheetToCsv(sheet)], { type: "text/csv;charset=utf-8" }),
    table.sheets.length === 1
      ? `${title || "table"}.csv`
      : `${title || "table"} - ${sheet.name}.csv`
  );
};

/** A text or document artifact's text, as its Download saves it. */
export const saveText = (text: string, filename: string): void =>
  saveBlobAs(new Blob([text], { type: "text/plain;charset=utf-8" }), filename);

/** Whether an artifact can be downloaded from a listing (no pane open): a
 *  table, a text or document artifact, a produced file. Not a diagram (its
 *  pane saves the drawing it renders) nor a live document (it has no
 *  file). */
export const canDownloadArtifact = (artifact: ArtifactItem): boolean =>
  !artifact.workspace_id &&
  artifact.kind !== "diagram" &&
  (artifact.kind === "table"
    ? artifact.content !== null
    : artifact.content !== null || artifact.filename !== null);

/** Downloads an artifact from a listing, as its pane's Download would. */
export const downloadArtifact = async (
  artifact: ArtifactItem
): Promise<void> => {
  if (artifact.kind === "table") {
    if (artifact.content === null) return;
    saveTableSheet(parseTableContent(artifact.content), 0, artifact.title);
    return;
  }
  const response = await fpFetch(artifactUrls(artifact.id).downloadUrl, {
    "x-finpilot-auth": true,
  });
  if (!response.ok)
    throw new Error(`${response.status}: ${response.statusText}`);
  const filename =
    artifact.filename ??
    (artifact.content !== null ? `${artifact.title}.md` : artifact.title);
  saveBlobAs(await response.blob(), filename);
};
